/**
 * The book, blinking in the corner, when you pick up something it knows about.
 *
 * A recipe book nobody opens is a recipe book nobody has. The things in this
 * game that are worth making are mostly not guessable - you find смола in a
 * pine trunk and there is nothing about a lump of resin that says "this is
 * half a смолоскип" - so the moment to say "look in the book" is the moment
 * the thing lands in your hand.
 *
 * It says **B** rather than E because B is the key that shows recipes: the
 * book is a column of the inventory window, and E opens that window with the
 * book in whatever state it was last left in. Naming the key that always
 * works is the whole point of a hint.
 *
 * Deliberately not a queue of messages. One nudge, however many new things
 * arrived, and it goes when you open the book or when you click it - a corner
 * that fills up with notifications is a corner people learn to ignore.
 */

import { ui, type Lang } from "../core/text/lang.js";

/** How long it stays up on its own, in milliseconds. */
const LINGER = 9000;

export class RecipeNudge {
  private readonly root: HTMLElement;
  private readonly textEl: HTMLElement;
  private until = 0;
  private timer: number | null = null;

  lang: Lang = "uk";

  constructor(host: HTMLElement) {
    this.root = document.createElement("div");
    this.root.className = "nudge";
    this.root.hidden = true;

    /*
     * The book, drawn out of four divs.
     *
     * Not an atlas tile: there is no book among the blocks, and the one in
     * the player's hands is a panel rather than an item. Four rectangles - a
     * dark spine, cream pages, a gold fore-edge and a line down the middle -
     * read as a closed book at forty pixels, which is all that is wanted.
     */
    const book = document.createElement("div");
    book.className = "nudge-book";
    for (const part of ["spine", "pages", "edge", "seam"]) {
      const el = document.createElement("div");
      el.className = `nudge-${part}`;
      book.append(el);
    }

    this.textEl = document.createElement("div");
    this.textEl.className = "nudge-text";

    this.root.append(book, this.textEl);
    // Clicking it is the other way to say "yes, I have seen it".
    this.root.addEventListener("click", () => this.hide());
    host.append(this.root);
    this.retitle();
  }

  retitle(): void {
    this.textEl.textContent = ui("nudge-recipes", this.lang);
  }

  /** Whether the nudge is currently on the screen. */
  get showing(): boolean {
    return !this.root.hidden;
  }

  /**
   * Put it up, or push its clock back if it is already up.
   *
   * Picking up a second new thing while the first nudge is still blinking
   * should not start a second nudge, and should not let the first one expire
   * a moment later either.
   */
  show(): void {
    this.root.hidden = false;
    this.until = performance.now() + LINGER;
    if (this.timer !== null) window.clearTimeout(this.timer);
    this.timer = window.setTimeout(() => this.lapse(), LINGER + 50);
  }

  hide(): void {
    this.root.hidden = true;
    this.until = 0;
    if (this.timer !== null) {
      window.clearTimeout(this.timer);
      this.timer = null;
    }
  }

  /**
   * The timer came round. Go only if nothing has pushed the clock back.
   *
   * A second `show` while this one was pending moves `until` forward without
   * cancelling anything, and the old timer must then not take the nudge down
   * early.
   */
  private lapse(): void {
    if (performance.now() >= this.until) this.hide();
  }
}
